import {safeSessionStorage} from '@utils/safeStorages';
import {useEffect, useState} from 'react';

import {footerPaddings} from '../common/footerPaddings';
import {
  defaultAutorefreshConfig,
  FLOOR_AD_CONTAINER_ID,
  FloorAdCodes,
  SESSION_STORAGE_KEY,
} from './consts';
import {
  useYandexAdsContextSelector,
  YandexAdsContextCommonSelectors,
} from './context/YandexAdsContext';
import {useAdFoxStats} from './hooks/useAdFoxStats';
import {useAnalyticsCallbacks} from './hooks/useAnalyticsCallbacks';
import {useApiAdapter} from './hooks/useApiAdapter';
import {useAutorefresh} from './hooks/useAutorefresh';
import {useRequestContextScriptLoad} from './hooks/useRequestContextScriptLoad';
import {AdFoxConfig, AdPlatformType} from './types';
import {combineAndExtendCallbacks} from './utils/combineAndExtendCallbacks';
import {getAdvManager} from './utils/getAdvManager';

type Props = {
  mode: 'floor_ad' | 'floor_ad_closed' | 'floor_ad_closed_copy';
  env: {
    YANDEX_ADS_FLOOR_AD_DESKTOP_ID: string;
    YANDEX_ADS_FLOOR_AD_MOBILE_ID: string;
    YANDEX_ADS_FLOOR_AD_ALT_DESKTOP_ID: string;
    YANDEX_ADS_FLOOR_AD_ALT_MOBILE_ID: string;
  };
  adFoxConfig: AdFoxConfig;
};

const FloorAd = ({mode, env, adFoxConfig}: Props) => {
  const adsDisabled = useYandexAdsContextSelector(
    YandexAdsContextCommonSelectors.getAdsDisabled,
  );
  const [adWasAlreadyClosedInSession] = useState(
    () => !!safeSessionStorage.getItem(SESSION_STORAGE_KEY),
  );
  const [adWasClosed, setAdWasClosed] = useState(false);

  const canShowAd =
    (!adWasAlreadyClosedInSession || mode === 'floor_ad') &&
    !adWasClosed &&
    !adsDisabled;

  const adPlatform: AdPlatformType = adFoxConfig.enabled ? 'adfox' : 'yandex';

  const scriptState = useRequestContextScriptLoad(adPlatform, canShowAd);

  const apiAdapter = useApiAdapter({
    adFoxConfig,
    containerId: FLOOR_AD_CONTAINER_ID,
    type: 'floorAd',
  });

  const createAnalyticsCallbacks = useAnalyticsCallbacks({
    platform: adPlatform,
    adType: mode,
  });

  const {createAutorefreshCallbacks, getRefreshNumber} = useAutorefresh({
    refresh: apiAdapter.refresh,
    containerId: FLOOR_AD_CONTAINER_ID,
    isFloorAd: true,
    enabled: scriptState === 'loaded' && canShowAd,
    config: adFoxConfig.enabled
      ? adFoxConfig.autorefresh
      : defaultAutorefreshConfig,
  });

  const createAdFoxStatsCallbacks = useAdFoxStats({getRefreshNumber});

  useEffect(() => {
    if (!canShowAd || scriptState !== 'loaded') {
      return;
    }
    let cleanupCallbacks: VoidFunction = () => {
      // noop
    };

    window.yaContextCb?.push(() => {
      const devicePlatform = getAdvManager().getPlatform();
      const adFoxCode = FloorAdCodes[devicePlatform];
      const blockId =
        devicePlatform === 'desktop'
          ? mode == 'floor_ad_closed_copy'
            ? env.YANDEX_ADS_FLOOR_AD_ALT_DESKTOP_ID
            : env.YANDEX_ADS_FLOOR_AD_DESKTOP_ID
          : mode === 'floor_ad_closed_copy'
            ? env.YANDEX_ADS_FLOOR_AD_ALT_MOBILE_ID
            : env.YANDEX_ADS_FLOOR_AD_MOBILE_ID;

      const {callbacks, cleanup} = combineAndExtendCallbacks({
        containerId: FLOOR_AD_CONTAINER_ID,
        adFoxCode,
        blockId,
        factories: [
          createAnalyticsCallbacks,
          createAutorefreshCallbacks,
          adPlatform === 'adfox' ? createAdFoxStatsCallbacks : null,
        ],
        callback: {
          onRender: (data) => {
            footerPaddings.add(data.floorAdSize?.height);
          },
          onFloorAdClose: () => {
            setAdWasClosed(true);
            footerPaddings.remove();
            safeSessionStorage.setItem(SESSION_STORAGE_KEY, '1');
          },
        },
      });

      cleanupCallbacks = cleanup;

      apiAdapter.init({
        callbacks,
        adFoxCode,
        blockId,
        devicePlatform,
      });
    });

    return () => {
      cleanupCallbacks();
      apiAdapter.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptState, canShowAd]);

  return <div id={FLOOR_AD_CONTAINER_ID} />;
};

export default FloorAd;
